import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Button } from './ui/button'
import { Cast } from 'lucide-react'
import { getOrCreateCastSession, pollCast } from '../cast'
import { setVideoIds, getVideoIds } from '../video'

const POLL_INTERVAL_MS = 5_000

export function CastSetup() {
  const [open, setOpen] = useState(false)
  const [session] = useState(() => getOrCreateCastSession())
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [castUrl, setCastUrl] = useState('')
  const [lastReceived, setLastReceived] = useState<string | null>(null)

  useEffect(() => {
    const url = `${window.location.origin}/cast/${session}`
    setCastUrl(url)
    QRCode.toDataURL(url, { width: 320, margin: 2, errorCorrectionLevel: 'M' })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null))
  }, [session])

  // Polling activo solo mientras el modal está abierto — evita gastar reads KV.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const tick = async () => {
      if (cancelled) return
      try {
        const { video_id } = await pollCast(session)
        if (!cancelled && video_id) {
          const current = getVideoIds()
          if (!current.includes(video_id)) {
            setVideoIds([...current, video_id])
          }
          setLastReceived(video_id)
        }
      } catch {
        // network errors silenciados; el próximo poll reintenta.
      }
    }
    const t = setInterval(tick, POLL_INTERVAL_MS)
    void tick()
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [open, session])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Agregar video desde el celular"
        onClick={() => setOpen(true)}
      >
        <Cast className="h-5 w-5" />
      </Button>
      <DialogContent aria-label="Cast video desde celular" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enviar video desde el celular</DialogTitle>
          <DialogDescription>
            Escanea el QR con tu celular y pega ahí la URL del video de YouTube.
            La TV lo agrega a la rotación automáticamente en segundos.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR para abrir el form de cast en el celular"
              className="rounded-md border border-zinc-800 bg-white p-2"
              data-testid="cast-qr-img"
            />
          ) : (
            <div className="h-[320px] w-[320px] animate-pulse rounded-md bg-zinc-800" />
          )}
          <input
            type="text"
            readOnly
            value={castUrl}
            aria-label="URL para el celular"
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs font-mono text-zinc-100"
          />
          {lastReceived && (
            <p data-testid="cast-received" className="text-sm text-green-400">
              Recibido: <code className="font-mono">{lastReceived}</code>
            </p>
          )}
          <p className="text-xs text-zinc-500 text-center">
            La sesión expira tras 5 minutos sin actividad. Puedes cerrar este modal cuando termines.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
